/**
 * Notifications Module - TanStack Query Hooks
 *
 * This module provides all hooks for notification-related operations including:
 * - Notifications retrieval
 * - Mark notifications as read
 * - Delete notifications
 * - Notification settings management
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { NotificationSettings } from "js/components/types";

import {
  deleteNotification,
  getNotifications,
  markNotificationAsRead,
  retrieveNotificationSettings,
  updateNotificationSettings,
} from "../api/notifications";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get all notifications
 * @returns Notifications list with loading and error states
 * Note: The API already sorts and adds timeSince property
 */
export const useGetNotifications = () => {
  return useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: getNotifications,
    staleTime: 30 * 1000, // 30 seconds - notifications are real-time
    refetchInterval: 60 * 1000, // Refetch every minute
  });
};

/**
 * Get notification settings
 * @returns Notification settings with loading and error states
 */
export const useGetNotificationSettings = () => {
  return useQuery({
    queryKey: queryKeys.notifications.settings(),
    queryFn: retrieveNotificationSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================================================
// MUTATION HOOKS (UPDATE, DELETE)
// ============================================================================

/**
 * Mark notification as read
 * Invalidates: notifications list
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      markNotificationAsRead(notificationId),
    onSuccess: () => {
      // Invalidate notifications list to refresh unread count
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.list(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al marcar la notificación como leída";
      toast.error(errorMessage);
    },
  });
};

/**
 * Delete a notification
 * Invalidates: notifications list
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => deleteNotification(notificationId),
    onSuccess: () => {
      toast.success("Notificación eliminada exitosamente");
      // Invalidate notifications list
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.list(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al eliminar la notificación";
      toast.error(errorMessage);
    },
  });
};

/**
 * Update notification settings
 * Invalidates: notification settings
 */
export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NotificationSettings) =>
      updateNotificationSettings(data),
    onSuccess: () => {
      toast.success("Configuración de notificaciones actualizada exitosamente");
      // Invalidate notification settings
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.settings(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al actualizar la configuración de notificaciones";
      toast.error(errorMessage);
    },
  });
};
